import axios from "axios";
import { createStore } from "vuex";

import attributeManager from "./AttributeManager/store/attributeManager";
import automationServer from "./AutomationServer/store/automationServer";
import channelCreator from "./ChannelCreator/store/channelCreator";
import contentEditor from "./ContentEditor/store/contentEditor";
import dashboard from "./Dashboard/store/dashboard";
import exports from "./Exports/store/exports";
import imports from "./Imports/store/import.ts";
import configurationManager from "./ConfigurationManager/store/configurationManager.ts";
import gpsr from "./Gpsr/store/gpsr";
import translationManager from "./TranslationManager/store/translationManager";
import userManager from "./UserManager/store/userManager";
import metadata from "./common/store/metadata";
import config from "./config/configResolver";

import authenticationApi from "./services/authentication";
import globalParametersApi from "./services/globalParameters";
import usersettingsapi from "./services/userSettings";
import usersApi from "./services/users";

import { Guid } from "./util/guid";
import getBrowserLocale from "./util/i18n/get-browser-locale";
import { getSupportedLocales } from "./util/i18n/supported-locales";
import eventLogger from "./services/ApiLogger/EventLogger";
import _ from "lodash";
import emitter from "tiny-emitter/instance";

const store = createStore({
  state: {
    user: {
      firstName: "",
      lastName: "",
      username: "",
      image: "",
      email: null,
      lastLogin: null,
      department: "",
      isAuthenticated: false,
      roles: [],
      isDeeplAuthorized: false,
      applicationPermissions: [],
      applicationLicenses: [],
      hasAdminRole: false
    },
    globalParameters: {
      IsAIActivated: null,
      IsErpActivated: null,
      PriceCurrency: null,
      IntervalSeparator: null,
      MultiAttributeValueSeparator: null,
      ExecuteMediaConversionAfterUpdateInContentEditor: null,
      ArticleDescriptionIsHTML: null,
      IsActivityFeedEnabled: null,
      IsTradeProNextEnabled: null,
      ExecuteMediaConversionBeforeExport: null,
      ExecuteMediaConversionAfterCumulusSync: null
    },
    licenses: {
      CustomerId: "",
      VersionId: "",
      ExpiryDate: "",
      CreationDate: "",
      Articles: "",
      Languages: "",
      NamedUsers: "",
      AIDesigner: "",
      AIImport: "",
      AITexter: "",
      AITranslator: "",
      Activity: "",
      AmazonConnector: "",
      App: "",
      AribaConnector: "",
      AutomationServer: "",
      BuildupConnector: "",
      Cms: "",
      CmsChannels: "",
      CommandLineNamedUsers: "",
      ConcurrentUsers: "",
      Connector2ba: "",
      Databases: "",
      DehaConnector: "",
      EdcConnector: "",
      ErpConnector: "",
      ErpConnectorArticles: "",
      FastPageConcurrentUsers: "",
      FastPageNamedUsers: "",
      FastPageServer: "",
      FlipCatalogCatalogs: "",
      FlipCatalogUsers: "",
      GPSR: "",
      Gs1Connector: "",
      IPadCatalogBookshelfEdition: "",
      IPadCatalogUsers: "",
      InfoSystem: "",
      InfoSystemDatabases: "",
      InfoSystemExternalUsers: "",
      InfoSystemInternalUsers: "",
      InfoSystemLanguages: "",
      MamDatabases: "",
      MamInternalUsers: "",
      MamLightUsers: "",
      MamReadOnlyExternalUsers: "",
      MamReadOnlyInternalUsers: "",
      MamUploadExternalUsers: "",
      Mdm: "",
      MdmArticles: "",
      MultiChannelConnector: "",
      PageFactory: "",
      PartnerDatabases: "",
      PdfCatalogUsers: "",
      PublishArticles: "",
      PublishCatalogBuilderConcurrentUsers: "",
      PublishCatalogBuilderNamedUsers: "",
      PublishConcurrentUsers: "",
      PublishDirectNamedUsers: "",
      PublishNamedUsers: "",
      Sync: "",
      TranslationManager: "",
      WebClientConcurrentFastPageUsers: "",
      WebClientNamedFastPageUsers: "",
      WebService: "",
      WebServiceChannels: "",
      WorkManager: "",
      WorkManagerNamedUsers: ""
    },
    auth: { token: "" },
    jwt: { token: "", tokenExpiry: 0, intervalId: null },
    meta: { loading: false, version: process.env.VUE_APP_VERSION, lastUpdated: "", config: config, sessionRestoreCompleted: false },

    userSettings: {
      AITexterLegalInformationAccepted: "false",
      ArticleEditorActiveTab: null,
      AttributeManagerActiveTab: null,
      AttributeManagerSelectedAttributeGroupId: null,
      CatalogFieldMappingSeparator: "|",
      ChannelCreatorSourceCatalogId: null,
      ChannelCreatorTargetCatalogId: null,
      ContentEditorSplitViewWidth: "",
      CurrentLocale: checkSupportedLocale(getBrowserLocale()),
      DeepLLegalInformationAccepted: "0",
      DefaultLiveEditLayout: "paperCatalog",
      EnableMediaAssetGalleryMode: "false",
      ErpAttributesFilterText: "",
      HideEmptyArticleMediaAssets: "false",
      HideFilledArticleMediaAssets: "false",
      HideConvertedMediaAssets: "false",
      GlobalAttributesFilterText: "",
      HideEmptyAttributesCatalogNode: false,
      HideEmptyAttributesGlobalAttributes: false,
      HideEmptyAttributesInvariantAttributes: false,
      HideEmptyAttributesVariantAttributes: false,
      HideEmptyAttributesMultiVariantAttributes: false,
      HideEmptyAttributesERP: false,
      HideEmptyCatalogMediaAssets: false,
      IsAbbreviationVisible: false,
      IsAlternativeNameVisible: "false",
      IsAttributegroupsDrawerCollapsedAttributeManager: false,
      IsSingleArticleMode: "true",
      IsCatalogNodeStatusVisible: "false",
      IsERPAttributeSuggestionsEnabled: "true",
      IsExpandAllNodesEnabled: "false",
      IsGlobalAttributeSuggestionsEnabled: "true",
      IsLiveEditEnabled: "true",
      IsLiveEditPaginationEnabled: "true",
      IsShowPricesEnabled: "true",
      IsShowingCataloguesTabEnabled: "true",
      IsShowingClassificationTabEnabled: "true",
      IsShowingERPTabEnabled: "true",
      IsShowingGPSRTabEnabled: "true",
      IsShowingGlobalAttributesTabEnabled: "true",
      IsShowingKeywordsTabEnabled: "true",
      IsShowingMasterDataTabEnabled: "true",
      IsShowingMediaAssetsTabEnabled: "true",
      IsShowingPricesTabEnabled: "true",
      IsShowingReferencesTabEnabled: "true",
      IsShowingTableAttributeTabEnabled: "true",
      IsShowingTextAttributeTabEnabled: "true",
      IsShowingTranslationTabEnabled: "true",
      IsSidebarOpen: "false",
      IsTextAttributeSuggestionsEnabled: "true",
      IsTableAttributeSuggestionsEnabled: "true",
      IsUidVisible: "false",
      LastOpenedCatalogId: Guid.empty,
      LiveEditArticlePagesize: 5,
      SelectedAttributeTypeInAM: "TextAttribute",
      ShowCatalogGroupArea: "false",
      ShowLiveEditHints: "true",
      ShowConfirmationDialogOnDragAndDrop: "true",
      ShowConfirmationDialogIfAttributeGroupDiffers: "true",
      ShowSourceCatalogTreeSearch: "false",
      ShowTargetCatalogTreeSearch: "false",
      TreeState: "",
      UsePredefinedNodeNames: "false",
      VariationGroupEditorActiveTab: null,
      IsInheritanceIndicatorVisible: false,
      ExcelEditAreas: ""
    },
    saveStateMeta: { isUnsaved: false, promises: [], errors: [], lastSuccessfulSave: null }
  },
  getters: {
    isAuthenticatedSession(state) {
      return !!(state.auth.token && state.user.username);
    },
    saveState({ saveStateMeta }) {
      if (saveStateMeta.promises?.length) return "saving";
      if (saveStateMeta.errors?.length) return "error";
      if (saveStateMeta.isUnsaved) return "unsaved";
      if (Date.now() - (saveStateMeta.lastSuccessfulSave ?? 0) <= 10000) return "saved";

      if (!saveStateMeta.isUnsaved && !saveStateMeta.promises.length && !saveStateMeta.errors.length && !saveStateMeta.lastSuccessfulSave) return "";
    }
  },
  mutations: {
    Login(state, args) {
      Object.assign(state.user, args.user);
      Object.assign(state.userSettings, args.settings);

      state.auth.token = args.token;
      axios.defaults.headers["Authorization"] = "Bearer " + args.token;
      state.user.isAuthenticated = true;
      emitter.emit("logged-in", { ...state.user });
      if (window.localStorage) {
        window.localStorage.setItem("mticket", JSON.stringify({ user: args.user, token: args.token }));
      }
    },
    Logout(state) {
      Object.assign(state.user, {
        firstName: "",
        lastName: "",
        username: "",
        isAuthenticated: false,
        roles: [],
        applicationPermissions: {},
        applicationLicenses: {}
      });
      state.userSettings = {};
      state.auth.token = "";
      delete axios.defaults.headers["Authorization"];
      emitter.emit("logged-out");
    },
    UpdateIdentity(state, args) {
      Object.assign(state.user, args);
    },
    CHANGE_INTERFACE_LANGUAGE(state, lang) {
      state.meta.language.interface = lang;
    },
    SessionRestored(state) {
      state.meta.sessionRestoreCompleted = true;
    },
    "load-user-settings"(state, args) {
      const settings = Object.assign({}, state.userSettings);

      //validating Settings
      args.CurrentLocale = checkSupportedLocale(args.CurrentLocale ? args.CurrentLocale : state.userSettings.CurrentLocale);

      state.userSettings = Object.assign(settings, args);
    },
    "save-user-settings"(state, args) {
      state.userSettings = args;
    },
    "load-global-parameters"(state, args) {
      if (args) {
        state.globalParameters.IsAIActivated = args.IsAIActivated?.toLowerCase() == "true";
        state.globalParameters.IsErpActivated = args.IsErpActivated?.toLowerCase() == "true";
        state.globalParameters.PriceCurrency = args.PriceCurrency;
        state.globalParameters.IntervalSeparator = args.IntervalSeparator;
        state.globalParameters.MultiAttributeValueSeparator = args.MultiAttributeValueSeparator;
        state.globalParameters.ExecuteMediaConversionAfterUpdateInContentEditor =
          args.ExecuteMediaConversionAfterUpdateInContentEditor?.toLowerCase() == "true";
        state.globalParameters.ArticleDescriptionIsHTML = args.ArticleDescriptionIsHTML?.toLowerCase() == "true";
        state.globalParameters.IsActivityFeedEnabled = args.IsActivityFeedEnabled?.toLowerCase() == "true";
        state.globalParameters.IsTradeProNextEnabled = args.IsTradeProNextEnabled?.toLowerCase() == "true";
        state.globalParameters.ExecuteMediaConversionBeforeExport = args.ExecuteMediaConversionBeforeExport?.toLowerCase() == "true";
        state.globalParameters.ExecuteMediaConversionAfterCumulusSync = args.ExecuteMediaConversionAfterCumulusSync?.toLowerCase() == "true";
      }
    },
    "set-licenses"(state, args) {
      state.licenses = args;
    },
    "save-global-parameters"(state, args) {
      state.globalParameters = args;
    },
    "add-saving-promise"(state, promise) {
      if (Array.isArray(promise)) state.saveStateMeta.promises.concat(promise);
      else state.saveStateMeta.promises.push(promise);
    },
    "set-unsaved-state"(state, isUnsaved = true) {
      state.saveStateMeta.isUnsaved = isUnsaved;
      if (isUnsaved) state.saveStateMeta.lastSuccessfulSave = null;
    },
    "refresh-jwt"(state, arg) {
      state.jwt.token = arg.data.jwt;
      const payload = JSON.parse(atob(arg.data.jwt.split(".")[1]));
      state.jwt.tokenExpiry = payload.exp * 1000; // convert to milliseconds
    },
    "set-interval-id"(state, id) {
      state.jwt.intervalId = id;
    },
    "clear-interval-id"(state) {
      if (state.jwt.intervalId) {
        clearInterval(state.jwt.intervalId);
        state.jwt.intervalId = null;
      }
    }
  },
  actions: {
    login: async function (context, args) {
      const details = await authenticationApi.login(args.username, args.password);
      return await context.dispatch("handleLogin", { details });
    },
    loginWithCode: async function (context, args) {
      const details = await authenticationApi.loginWithCode(args.provider, args.code);
      return await context.dispatch("handleLogin", { details });
    },
    getValidJwt: async function ({ state, dispatch }) {
      const now = Date.now();

      // If not set or expired (give a buffer of 6 minutes)
      if (!state.jwt.token || now > state.jwt.tokenExpiry - 6 * 60 * 1000) {
        console.log("Token expired or missing, refreshing...");
        await dispatch("getJwt", {});
      }
    },
    getJwt: async function (context) {
      const data = await authenticationApi.getJwt();

      context.commit("clear-interval-id");
      const intervalId = window.setInterval(
        () => {
          context.dispatch("getValidJwt");
        },
        8 * 60 * 60 * 1000
      );
      context.commit("set-interval-id", intervalId);

      context.commit("refresh-jwt", {
        data: data
      });
    },
    handleLogin: async function ({ state, commit, dispatch }, args) {
      if (!args.details.user.isWebClientLicensed) {
        throw { licenseError: true, data: args.details };
      }

      const defaultSettingsNames = Object.keys(state.userSettings);
      const SettingsNamesFromDb = Object.keys(args.details.settings);

      defaultSettingsNames.forEach((name) => {
        const oldName = SettingsNamesFromDb.find((x) => x.toLowerCase() == name.toLowerCase() && x !== name);
        if (oldName) {
          args.details.settings[name] = args.details.settings[oldName];
          delete args.details.settings[oldName];
        }
      });

      commit("load-user-settings", args.details.settings);
      commit("Login", args.details);
      // Check if the user has all the user settings rather than just the default ones and save if not.
      if (Object.keys(args.details.settings).length <= 2) {
        await store.dispatch("saveUserSettings", state.userSettings);
      }

      await dispatch("metadata/loadLanguages");
      //Load global parameters
      await dispatch("loadGlobalParameters", [
        "IsAIActivated",
        "IsErpActivated",
        "PriceCurrency",
        "IntervalSeparator",
        "MultiAttributeValueSeparator",
        "ExecuteMediaConversionAfterUpdateInContentEditor",
        "ArticleDescriptionIsHTML",
        "IsActivityFeedEnabled",
        "IsTradeProNextEnabled",
        "ExecuteMediaConversionBeforeExport",
        "ExecuteMediaConversionAfterCumulusSync"
      ]);

      commit("set-licenses", await dispatch("getLicenses"));
      await dispatch("getValidJwt", {});

      return args.details.user;
    },
    logout: async function (context) {
      try {
        await authenticationApi.logout();
      } catch (error) {
        console.error(error);
      }
      window.localStorage.removeItem("mticket");
      context.commit("Logout");
      context.commit("contentEditor/article/reset");
      context.commit("contentEditor/variationGroup/reset-state");
    },
    tryRestore: async function ({ state, commit, dispatch }) {
      if (window.localStorage) {
        try {
          const user = window.localStorage.getItem("mticket");
          const record = tryParse(user);
          if (record) {
            commit("UpdateIdentity", record.user);
            const details = await authenticationApi.loginWithToken(record.token);

            if (!details.user.isWebClientLicensed) {
              throw { licenseError: true, data: details };
            }

            const defaultSettingsNames = Object.keys(state.userSettings);
            const SettingsNamesFromDb = Object.keys(details.settings);

            defaultSettingsNames.forEach((name) => {
              const oldName = SettingsNamesFromDb.find((x) => x.toLowerCase() == name.toLowerCase() && x !== name);
              if (oldName) {
                details.settings[name] = details.settings[oldName];
                delete details.settings[oldName];
              }
            });

            commit("load-user-settings", details.settings);
            commit("Login", details);
            await dispatch("metadata/loadLanguages");
            await dispatch("loadGlobalParameters", [
              "IsAIActivated",
              "IsErpActivated",
              "PriceCurrency",
              "IntervalSeparator",
              "MultiAttributeValueSeparator",
              "ExecuteMediaConversionAfterUpdateInContentEditor",
              "ArticleDescriptionIsHTML",
              "IsActivityFeedEnabled",
              "IsTradeProNextEnabled",
              "ExecuteMediaConversionBeforeExport",
              "ExecuteMediaConversionAfterCumulusSync"
            ]);
            commit("SessionRestored");

            commit("set-licenses", await dispatch("getLicenses"));
            await dispatch("getValidJwt", {});
            return details.user;
          } else {
            commit("clear-interval-id");
            eventLogger.info("Medapro user from local storage", "Invalid user.");
          }
        } catch (error) {
          commit("clear-interval-id");
          if (error && error.isAxiosError && error.response.status && error.response.status === 401) {
            console.warn("session expired. Removing existing session information");
            window.localStorage.removeItem("mticket");
          } else {
            eventLogger.error("Medapro restore localstorage", "Restore failed.", error);
          }
        }
      } else {
        console.error("localstorage not available");
      }
      commit("SessionRestored");
      throw { success: false, feedback: "Restore data unavailable" };
    },
    getLicenses() {
      return axios.get("/api/Account/Licenses").then(function (resp) {
        return resp.data;
      });
    },
    getUserLicenses() {
      return axios.get("/api/Account/UserLicenses").then(function (resp) {
        return resp.data;
      });
    },
    saveUserSettings: async function ({ commit, state }, args) {
      try {
        const defaultSettingsNames = Object.keys(state.userSettings);
        const newSettings = Object.keys(args);
        const settings = Object.assign({}, state.userSettings);

        defaultSettingsNames.forEach((name) => {
          const newName = newSettings.find((x) => x.toLowerCase() == name.toLowerCase() && x !== name);
          if (newName) {
            settings[newName] = settings[name];
            delete settings[name];
          }
        });

        //Object.assign(settings, args);
        let hasNewChanges = false;
        newSettings.forEach((key) => {
          const newValue = args[key]?.toString();
          if (settings[key] !== newValue) {
            settings[key] = newValue;
            hasNewChanges = true;
          }
        });

        if (!hasNewChanges) {
          return;
        }

        commit("save-user-settings", settings);
        await usersettingsapi.save(settings);
      } catch (error) {
        if (!error || !error.handled) console.error("error on saving settings", error);
      }
    },
    loadUserProfile: async function ({ commit }, updateState) {
      const profile = await usersApi.loadProfile();
      if (updateState) commit("UpdateIdentity", profile);

      return profile;
    },
    loadGlobalParameters: async function ({ commit }, args) {
      try {
        const globalParameters = await globalParametersApi.get(args);
        commit("load-global-parameters", globalParameters?.globalParameters);
      } catch (error) {
        console.error("error on loading global parameters", error);
      }
    },
    saveGlobalParameters: async function ({ commit, state }, args) {
      try {
        const storeParameterNames = Object.keys(state.globalParameters);
        const updatedParameterNames = Object.keys(args);
        const globalParameters = Object.assign({}, state.globalParameters);

        storeParameterNames.forEach((name) => {
          const hasNewValue = updatedParameterNames.find((x) => x.toLowerCase() == name.toLowerCase());
          if (hasNewValue) {
            globalParameters[name] = args[name];
          }
        });

        commit("save-global-parameters", globalParameters);
        await globalParametersApi.save(args);
      } catch (error) {
        if (!error || !error.handled) console.error("error on saving global parameters", error);
        throw error;
      }
    },
    resetModuleStore: function ({ commit }, moduleName) {
      switch (moduleName) {
        case "dashboard":
          break;

        case "content-editor":
          commit("contentEditor/catalogNode/reset-state", null, { root: true });
          commit("contentEditor/variationGroup/reset-state", null, { root: true });
          commit("contentEditor/article/reset-state", null, { root: true });
          break;

        default:
          commit(`${_.camelCase(moduleName)}/reset-state`, null, { root: true });
          break;
      }
    },
    cancelActionsIfAny: async function () {
      Object.keys(this._modulesNamespaceMap).forEach((moduleKey) => {
        // Call the action if it exists in the child store module
        if (this._actions[`${moduleKey}cancelActionsIfAny`]) {
          this.dispatch(`${moduleKey}cancelActionsIfAny`, null, { root: true });
        }
      });
    },

    getSavePromiseResultAndResolve: async function ({ state }) {
      try {
        await Promise.all(state.saveStateMeta.promises);
        await new Promise((resolve) => {
          setTimeout(() => {
            if (state.saveStateMeta.promises.length) {
              state.saveStateMeta.promises = [];
              state.saveStateMeta.isUnsaved = false;
              state.saveStateMeta.lastSuccessfulSave = Date.now();
              setTimeout(() => {
                state.saveStateMeta.lastSuccessfulSave = null;
              }, 10000);
            }
            resolve();
          }, 300);
        });
        state.saveStateMeta.errors = [];
      } catch (errors) {
        state.saveStateMeta.errors.push(errors);
        state.saveStateMeta.promises = [];
        console.error(`Error(s) during saving`, state.saveStateMeta.errors);
      }
    }
  },
  modules: {
    metadata,
    dashboard,
    contentEditor,
    channelCreator,
    attributeManager,
    exports,
    imports,
    configurationManager,
    translationManager,
    gpsr,
    userManager,
    automationServer
  }
});

function tryParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function checkSupportedLocale(locale) {
  let result = locale;
  //checking if Language is supported
  const supportedLocales = getSupportedLocales().map((x) => x.code);
  const isSupportedLanguage = supportedLocales.includes(locale);
  if (!isSupportedLanguage) {
    result = supportedLocales[0] ?? "en";
  }
  return result;
}

export default store;
